import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

export type ConsentPreferences = {
  necessary: true; // always on
  analytics: boolean;
  marketing: boolean;
};

type StoredConsent = {
  preferences: ConsentPreferences;
  timestamp: string;
  version: number;
};

const STORAGE_KEY = "sprankelien.cookie-consent";
const CONSENT_VERSION = 1;

type ConsentContextValue = {
  preferences: ConsentPreferences;
  hasDecided: boolean;
  isSettingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (prefs: Omit<ConsentPreferences, "necessary">) => void;
  resetConsent: () => void;
};

const defaultPreferences: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext<ConsentContextValue | undefined>(undefined);

const readStored = (): StoredConsent | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const persistStored = (preferences: ConsentPreferences) => {
  if (typeof window === "undefined") return;
  const payload: StoredConsent = {
    preferences,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent("cookie-consent-change", { detail: preferences }));
};

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const [hasDecided, setHasDecided] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setPreferences({ ...stored.preferences, necessary: true });
      setHasDecided(true);
    }
  }, []);

  const openSettings = useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

  const commit = useCallback((next: ConsentPreferences) => {
    setPreferences(next);
    setHasDecided(true);
    persistStored(next);
  }, []);

  const acceptAll = useCallback(() => {
    commit({ necessary: true, analytics: true, marketing: true });
    setIsSettingsOpen(false);
  }, [commit]);

  const rejectAll = useCallback(() => {
    commit({ necessary: true, analytics: false, marketing: false });
    setIsSettingsOpen(false);
  }, [commit]);

  const savePreferences = useCallback(
    (prefs: Omit<ConsentPreferences, "necessary">) => {
      commit({ necessary: true, ...prefs });
      setIsSettingsOpen(false);
    },
    [commit]
  );

  const resetConsent = useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    setPreferences(defaultPreferences);
    setHasDecided(false);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      preferences,
      hasDecided,
      isSettingsOpen,
      openSettings,
      closeSettings,
      acceptAll,
      rejectAll,
      savePreferences,
      resetConsent,
    }),
    [preferences, hasDecided, isSettingsOpen, openSettings, closeSettings, acceptAll, rejectAll, savePreferences, resetConsent]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = () => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
};
